import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MetaHead from "./MetaHead";
import JournalForm, { JournalFormData } from "./JournalForm";
import AppSidebar from "./AppSidebar";
import { getTodayString } from "../utils/dateUtils";
import { useAppDispatch } from "../store/hooks";
import { createJournalEntry } from "../store/slices/journalSlice";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import { useToastContext } from "./ToastContext";

const AddJournalEntry: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const { addToast } = useToastContext();

  // Get date from URL search params
  const dateParam = searchParams.get("date");

  // Initial form data
  const initialData: JournalFormData = {
    title: "",
    content: "",
    date: dateParam || getTodayString(),
    tags: "",
    mood: "neutral",
  };

  useEffect(() => {
    trackPageVisit("add_journal_entry");
  }, []);

  const handleSubmit = async (data: JournalFormData) => {
    setLoading(true);

    try {
      // Process tags
      const tagsArray = data.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      // Dispatch createJournalEntry action to Redux store
      const result = await dispatch(
        createJournalEntry({
          title: data.title.trim(),
          content: data.content.trim(),
          date: data.date,
          tags: tagsArray.length > 0 ? tagsArray : undefined,
          mood: data.mood,
        }),
      ).unwrap();

      // Show toast if points earned
      if (result.pointsEarned > 0) {
        addToast(
          t("toast.pointsEarned", { points: result.pointsEarned }),
          "success",
        );
      }

      // Track journal entry creation event
      trackUserInteraction(
        "create_journal_entry",
        "journal_management",
        result?.id || "unknown",
      );

      // Navigate to day page
      navigate(`/day/${data.date}`);
    } catch (error) {
      console.error("Error saving journal entry:", error);
      // TODO: Show error message to user
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/app");
  };

  return (
    <>
      <MetaHead
        title={t("addJournalEntry.title")}
        description={t("addJournalEntry.description")}
        keywords={t("addJournalEntry.keywords")}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        <div className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* TOPBAR */}
          <header className="flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleCancel}
              aria-label={t("addJournalEntry.ariaLabel") as string}
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#e87b3f]/20 border border-[#e87b3f]/30 flex items-center justify-center transition-all hover:bg-[#e87b3f]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#e87b3f"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("addJournalEntry.headerTitle")}
            </p>
            <div className="w-8" />
          </header>

          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="max-w-3xl mx-auto pb-8 space-y-4">
              <div className="rounded-2xl border border-[#e87b3f]/20 dark:border-[#e87b3f]/30 bg-gradient-to-br from-[#e87b3f]/[0.07] to-transparent dark:from-[#e87b3f]/[0.09] p-4 lg:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#e87b3f]/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[24px] leading-none">📔</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[19px] font-semibold tracking-tight text-gray-900 dark:text-white truncate">
                        {t("addJournalEntry.headerTitle")}
                      </p>
                      <p className="text-[16px] text-gray-600 dark:text-white/45 leading-relaxed">
                        {t("addJournalEntry.description")}
                      </p>
                    </div>
                  </div>
                  <span className="text-[15px] font-semibold px-2.5 py-1 rounded-full shrink-0 bg-[#e87b3f]/20 border border-[#e87b3f]/30 text-[#e87b3f]">
                    {initialData.date}
                  </span>
                </div>
              </div>

              <div className="">
                <JournalForm
                  initialData={initialData}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  submitButtonText={t("addJournalEntry.saveJournalEntry")}
                  loading={loading}
                  title={t("addJournalEntry.headerTitle")}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddJournalEntry;
